import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { breadcrumbSchema, faqSchema, personSchema, webPageSchema } from "@/lib/seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { NetworkNextStep } from "@/components/NetworkNextStep";

const PATH = "/books/the-havoc-in-your-head";
const BOOK_ID = `https://shariqrefai.com${PATH}#book`;
const TITLE =
  "The Havoc in Your Head, by Shariq Refai, MD, MBA";
const DESCRIPTION =
  "A board-certified psychiatrist's reset for the moment anxiety, panic, and overthinking take over. The Havoc in Your Head, by Shariq Refai, MD, MBA.";
const OG_IMAGE = "/og-havoc.jpg";

const faqs = [
  {
    question: "When will The Havoc in Your Head be released?",
    answer:
      "The book is in active development with a 2026 release window through shrinkMD Publishing. Sign up to be notified the moment it's ready.",
  },
  {
    question: "Will this book stop my panic attacks?",
    answer:
      "No. The book is general educational reading and does not promise any specific outcome, symptom reduction, or clinical result. It describes an educational framework for thinking about an activated nervous system. It is not a treatment, not a substitute for professional care, and not appropriate as a replacement for evaluation and care from a licensed clinician. Panic disorder is a clinical condition that should be evaluated and managed by a qualified professional.",
  },
  {
    question: "How is The Havoc in Your Head different from Your Mind Is Full of Sh*t?",
    answer:
      "The first book is the framework, the why. The Havoc in Your Head is the in-the-moment response, the what to do when. The first book gives you the conceptual map. This one gives you the action under pressure.",
  },
  {
    question: "Do I need to read Your Mind Is Full of Sh*t first?",
    answer:
      "No. The book stands on its own. The reset framework is fully explained in this book without requiring the first one. Readers who want the deeper conceptual context can read the first book afterwards.",
  },
  {
    question: "Is the shrinkMD Reset a clinical protocol?",
    answer:
      "No. The reset is an educational framework. It's not a clinical intervention. It's not therapy. It's not a treatment protocol. It's a structured way of thinking about nervous system regulation, written for the general public.",
  },
  {
    question: "Will it help with intrusive thoughts or OCD?",
    answer:
      "The book includes a chapter on intrusive thoughts and the basic distinction between an OCD pattern and everyday rumination. For OCD specifically, the gold-standard treatment is exposure and response prevention with a trained clinician, sometimes alongside medication. The book is educational and is best used as a supplement to that care.",
  },
  {
    question: "Does the book help with depression?",
    answer:
      "The book covers depression at the framework level, including the body-first response when low mood and shutdown patterns hit. For moderate to severe depression, the book is a supplement to professional care, not a replacement.",
  },
  {
    question: "Is this related to shrinkMD?",
    answer:
      "The author founded shrinkMD and the book is published through shrinkMD Publishing. The reset framework shares the name. The book is educational. shrinkMD is the separate clinical platform. The two are related by author and brand, not by service.",
  },
  {
    question: "Will there be an audiobook?",
    answer:
      "Audiobook details will be announced at launch. The email list is the fastest way to be notified.",
  },
  {
    question: "How long does the reset take in practice?",
    answer:
      "The four-step reset is designed to run in under sixty seconds. Some readers find it takes a few practiced rounds before it lands quickly. Like any nervous system practice, the more you run it, the faster it runs.",
  },
  {
    question: "When should someone need more than this book?",
    answer:
      "If your symptoms are interfering with your ability to work, sleep, eat, or be around the people you love. If you're having repeated panic attacks. If you've been carrying intrusive thoughts you can't shift. If you're using substances to manage the load. If you're in crisis. The book is a supplement to professional care, never a replacement.",
  },
];

const bookJsonLd = {
  "@context": "https://schema.org",
  "@type": "Book",
  "@id": BOOK_ID,
  name: "The Havoc in Your Head",
  headline:
    "A Psychiatrist's Reset for Anxiety, Overthinking, Panic, and the Thoughts That Won't Let Go",
  abstract:
    "An in-the-moment response framework for activated nervous systems, written by a board-certified psychiatrist for readers whose typical advice fails when they actually need it.",
  author: { "@id": "https://shariqrefai.com/#person" },
  creator: { "@id": "https://shariqrefai.com/#person" },
  publisher: {
    "@type": "Organization",
    name: "shrinkMD Publishing",
    url: "https://shariqrefai.com/projects",
    memberOf: { "@id": "https://shrinknetwork.com/#organization" },
  },
  bookFormat: "https://schema.org/Hardcover",
  datePublished: "2026",
  inLanguage: "en",
  genre: ["Psychology", "Self-Help", "Mental Health", "Anxiety", "Panic Disorder"],
  audience: {
    "@type": "Audience",
    audienceType:
      "Adults experiencing anxiety, panic, intrusive thoughts, or overthinking who want an in-the-moment response framework",
  },
  about: [
    { "@type": "Thing", name: "Anxiety" },
    { "@type": "Thing", name: "Panic disorder" },
    { "@type": "Thing", name: "Overthinking" },
    { "@type": "Thing", name: "Intrusive thoughts" },
    { "@type": "Thing", name: "Nervous system regulation" },
    { "@type": "Thing", name: "Body-first regulation practices" },
    { "@type": "Thing", name: "Vagal tone" },
    { "@type": "Thing", name: "Affect labeling" },
  ],
  image: `https://shariqrefai.com${OG_IMAGE}`,
  url: `https://shariqrefai.com${PATH}`,
};

export default function BookHavoc() {
  const page = webPageSchema({
    path: PATH,
    title: TITLE,
    description: DESCRIPTION,
    image: OG_IMAGE,
    speakableSelectors: [".quick-answer", ".faq"],
    datePublished: "2026-04-01",
    dateModified: "2026-05-08",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Books", path: "/books" },
      { name: "The Havoc in Your Head", path: PATH },
    ],
  });
  const faqLd = faqSchema(faqs);
  return (
    <SiteLayout>
      <SEO
        title={TITLE}
        ogTitle="The Havoc in Your Head"
        twitterTitle="The Havoc in Your Head"
        description={DESCRIPTION}
        url={PATH}
        image={OG_IMAGE}
        type="book"
        keywords="The Havoc in Your Head, anxiety book, panic book, overthinking book, psychiatrist book, nervous system regulation, shrinkMD reset"
        schema={[
          personSchema,
          page,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Books", path: "/books" },
            { name: "The Havoc in Your Head", path: "/books/the-havoc-in-your-head" },
          ]),
          bookJsonLd,
          faqLd,
        ]}
      />

      {/* Hero */}
      <section className="hero-book pt-24 pb-16 md:pt-32 md:pb-20 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-6xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "Books", href: "/books" },
              { name: "The Havoc in Your Head" },
            ]}
          />
        </div>
        <div className="container mx-auto max-w-6xl grid lg:grid-cols-[1.4fr_1fr] gap-12 lg:gap-16 items-start">
          <div className="flex flex-col gap-6">
            {/* Credibility eyebrow */}
            <p className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-accent">
              Book · Shariq Refai, MD, MBA · Board-Certified Psychiatrist
            </p>
            <span className="status-pill self-start text-xs font-bold tracking-widest uppercase text-accent border border-accent/40 px-3 py-1.5 rounded-sm">
              In Development · 2026
            </span>
            <h1 className="text-3xl md:text-5xl font-serif text-primary leading-[1.1] tracking-tight">
              The Havoc in Your Head
            </h1>
            <p className="lede text-xl md:text-2xl font-serif italic text-secondary leading-snug">
              When your mind takes over, knowing what's happening logically doesn't stop it. This is the in-the-moment response. A four-step reset, built by a psychiatrist, designed to run in under sixty seconds, for the part of the day when you actually need help.
            </p>
            <div className="cta-row flex flex-wrap gap-4 mt-2">
              <Link
                href="/contact"
                className="btn-primary inline-flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 transition-colors px-6 py-3 text-sm font-medium tracking-wide rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                Notify Me at Launch
              </Link>
              <Link
                href="/books/your-mind-is-full-of-shit"
                className="btn-secondary inline-flex items-center justify-center border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors px-6 py-3 text-sm font-medium tracking-wide rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                View the First Book
              </Link>
            </div>
          </div>
          <div className="flex justify-center">
            <div className="w-full max-w-sm aspect-[2/3] bg-secondary flex flex-col items-center justify-center p-8 text-center shadow-xl">
              <span className="text-sm tracking-widest uppercase text-white/50">Cover coming soon</span>
              <h3 className="font-serif text-2xl text-white mt-4">The Havoc in Your Head</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Answer */}
      <section className="quick-answer py-12 md:py-16 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">Quick Answer</h2>
          <p className="text-lg text-secondary font-light leading-relaxed">
            <em>The Havoc in Your Head</em> is a 2026 book by Shariq Refai, MD, MBA, a board-certified psychiatrist. The book is the in-the-moment response companion to <em>Your Mind Is Full of Sh*t</em>. It introduces a body-first framework called the shrinkMD Reset, designed to interrupt anxiety, panic, overthinking loops, and intrusive thoughts in real time. The book is for readers who've already tried thinking their way out and need something that works when the body has already shifted.
          </p>
        </div>
      </section>

      {/* Opening hook */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-muted/20 border-y border-border/40">
        <div className="container mx-auto max-w-3xl prose prose-lg prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-p:font-light prose-p:text-secondary max-w-none">
          <h2>The 3 A.M. Patient</h2>
          <p>A patient sat across from me one afternoon and tried to explain it three different ways. He said his anxiety was the worst it had been in years. Then he said it had nothing to do with anxiety, that something was actually wrong with him. Then he said he didn't know what he was talking about, that maybe it was just stress, and could I please tell him how to stop overthinking.</p>
          <p>He was a thirty-six-year-old engineer. He'd been on the same antidepressant for two years, which seemed to be helping. He had a wife, two kids, and a mortgage that was almost paid off. He was, by his own description, fine.</p>
          <p>The reason he was sitting in front of me was that he'd started waking up at three in the morning with his chest tight and his heart pounding. He'd lie in bed for forty-five minutes, sometimes longer, trying to figure out what was wrong. He'd run through his finances. He'd run through his job. He'd run through his marriage. He'd find nothing actually wrong, and the panic would eventually fade, and he'd fall back asleep around five-thirty.</p>
          <p>He told me he'd been doing this almost every night for three weeks.</p>
          <p>"I don't know what's wrong with me," he said. "I have a good life."</p>
          <p>I asked him a question I've learned to ask early. "When you wake up, what does your body feel like before you start thinking?"</p>
          <p>He paused. "It feels like something happened to it. Like I just got off a treadmill. Heart's already going. Breathing's already off."</p>
          <p>"So what time does the thinking actually start?"</p>
          <p>He looked at the ceiling for a moment. "After. After I notice my heart."</p>
          <p>That answer is one of the most important sentences a person can say in a psychiatry office, and most people don't know it when they say it. His body had already shifted. The thoughts arrived after.</p>
          <p>That order of operations is what changes how you treat anxiety. Not the diagnosis. Not the medication. The sequence. <em>The Havoc in Your Head</em> is the book about that sequence.</p>

          <h2>Why Thinking Harder Doesn't Work</h2>
          <p>Most popular mental health commentary is calm-brain advice given to a panicked brain. "Just breathe." "Challenge your thoughts." "Reframe it." None of it is wrong. All of it is sequenced wrong.</p>
          <p>When the body is in a high-activation state, the brain's capacity for deliberate cognitive control is reduced. Neuroimaging studies suggest the prefrontal cortex, the part of the brain that does careful reasoning, becomes harder to engage, and the amygdala, the brain's threat detection system, becomes more reactive. The picture is more complex than a simple flip between regions, but the practical implication is the same. Thinking-based tools work less well in the middle of a surge. The body is already in motion. Trying to reason your way out is like trying to talk yourself warm. You can read the temperature off a thermostat all day. The body doesn't care. The body needs an actual sweater.</p>
          <p>The cognitive tools work after the body has settled enough to use them. Not before. The first part of the work, the part most people skip, is regulation. Once the body is back online, the thinking tools are useful. The book is built around that order.</p>
        </div>
      </section>

      {/* Reset Framework */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">The shrinkMD Reset Framework</h2>
          <p className="text-lg text-secondary font-light leading-relaxed mb-8">
            The book introduces a four-step reset designed to run in under sixty seconds. It's not magic. It's calibrated to the way the nervous system actually works.
          </p>
          <div className="space-y-8">
            {[
              {
                h: "Step 1, Body First",
                p: "One real input. A longer exhale than inhale. A foot pressed firmly into the floor. A drink of cold water. Not five things. One. The body learns from small repeatable inputs, not heroic interventions.",
              },
              {
                h: "Step 2, Sensory Orientation",
                p: "Three specific things you can see in the room you're actually in. Not categories. Specifics. The pen. The clock. The corner of the rug. The amygdala calms when the prefrontal cortex starts noticing the body and the room.",
              },
              {
                h: "Step 3, Naming",
                p: "Out loud, in plain language. \"My chest is tight. My breath is high. There's no actual emergency. My body is running weather.\" Affect labeling reduces amygdala activity within seconds. The brain settles when the body has language.",
              },
              {
                h: "Step 4, Choose",
                p: "Now, and only now, you have access to the rest of your brain. Now you can choose what to do next. Argue with the thought, or let it pass. Stay where you are, or move. Reach for the next move, or let the wave finish.",
              },
            ].map((s) => (
              <div key={s.h}>
                <h3 className="text-xl font-serif text-primary mb-2">{s.h}</h3>
                <p className="text-secondary font-light leading-relaxed">{s.p}</p>
              </div>
            ))}
          </div>
          <p className="text-secondary font-light leading-relaxed mt-8">
            That's the reset. The book walks through each step in detail, with examples for anxiety, panic, overthinking, intrusive thoughts, and the specific moments most people get stuck.
          </p>
        </div>
      </section>

      {/* When to Use */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-muted/20 border-y border-border/40">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">When to Use the Reset</h2>
          <p className="text-lg text-secondary font-light leading-relaxed mb-6">
            The book covers the moments people actually need help.
          </p>
          <ul className="space-y-3 text-secondary font-light leading-relaxed list-disc pl-6">
            <li>The 3 a.m. wake-up with the chest tight and the mind racing.</li>
            <li>The Sunday-night surge before the week starts.</li>
            <li>The pre-meeting heat that won't settle.</li>
            <li>The grocery-store panic that came out of nowhere.</li>
            <li>The intrusive thought that wraps itself around your day.</li>
            <li>The post-argument loop you can't put down.</li>
            <li>The medical-test waiting period when every twinge feels like a verdict.</li>
            <li>The job-interview pressure that turns your hands cold.</li>
          </ul>
          <p className="text-secondary font-light leading-relaxed mt-6">
            For each, the book maps the body's specific shift, the typical thinking that follows, and the version of the reset that fits the moment.
          </p>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">Who This Book Is For</h2>
          <p className="text-lg text-secondary font-light leading-relaxed mb-4">
            This book is for the person who finds that typical mental health commentary falls short the moment they actually need it. The reader who knows about cognitive distortions and still can't stop the loop at midnight. The person who has read the books, done the courses, and watched all the techniques fall apart when their body was already in motion. The reader looking for something that works when the wave has already started.
          </p>
          <p className="text-secondary font-light leading-relaxed">
            It's also for readers who've been told they have generalized anxiety, panic disorder, OCD, or PTSD and want a clinician-written explanation of what's actually happening in the body and what to do about it in real time. The book is educational. It sits alongside professional care. It doesn't replace it.
          </p>
        </div>
      </section>

      {/* Series */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-muted/20 border-y border-border/40">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-4">The Series</h2>
          <p className="text-lg text-secondary font-light leading-relaxed">
            <em>The Havoc in Your Head</em> is the third book in a three-book set, and the in-the-moment companion to the framework. The first book,{" "}
            <Link href="/books/your-mind-is-full-of-shit" className="text-accent underline hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm">
              <em>Your Mind Is Full of Sh*t</em>
            </Link>
            , sets up the patterns and the wiring. The{" "}
            <Link href="/books/your-mind-is-full-of-shit-workbook" className="text-accent underline hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm">
              workbook
            </Link>{" "}
            is for hands-on observation. <em>The Havoc in Your Head</em> is the response when the patterns hit. Each book stands alone. Together they form a complete framework for the modern brain.
          </p>
        </div>
      </section>

      {/* Notify CTA */}
      <section id="notify" className="cta-final py-16 md:py-20 px-4 md:px-8 bg-primary text-primary-foreground">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-serif mb-4">Be Notified When the Book Is Available</h2>
          <p className="text-lg font-light leading-relaxed mb-8 opacity-90">
            The book is in active development. Sign up to be notified the moment it's ready, alongside excerpts and pre-order availability.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center justify-center bg-accent text-white hover:bg-accent/90 transition-colors px-8 py-3 text-sm font-medium tracking-wide rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
          >
            Request Launch Notification
          </Link>
        </div>
      </section>

      {/* Composite-patient narrative disclosure */}
      <aside
        role="note"
        aria-label="Composite patient narrative disclosure"
        className="composite-disclaimer py-8 px-4 md:px-8 bg-background"
      >
        <div className="container mx-auto max-w-3xl border-l-2 border-accent pl-6 py-4 bg-muted/30">
          <p className="text-sm text-secondary font-light leading-relaxed">
            <strong className="text-primary font-medium">A note on patient examples.</strong>{" "}
            Any patient examples, "I had a patient" framings, or
            clinical-style anecdotes in this book are educational
            composites. They blend and alter details from multiple
            situations and do not describe any identifiable individual or
            any actual patient of Dr. Refai or shrinkMD. They are
            presented for educational illustration only.
          </p>
        </div>
      </aside>

      {/* FAQ */}
      <section className="faq py-12 md:py-16 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-serif text-primary mb-8">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left font-serif text-lg">{f.question}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-light leading-relaxed text-base">
                  {f.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 px-4 md:px-8 bg-muted/30 border-t border-border/50">
        <div className="container mx-auto max-w-3xl">
          <h3 className="text-lg font-serif text-primary mb-3">Important Educational Disclaimer</h3>
          <p className="text-sm text-secondary font-light leading-relaxed">
            The content on this website is provided for general educational and informational purposes only and is not medical advice or a substitute for individualized psychiatric or medical care.
          </p>
          <p className="text-sm text-secondary font-light leading-relaxed mt-3">
            Viewing this website, reading its content, or submitting information through the website does not establish a physician-patient relationship.
          </p>
          <p className="text-sm text-secondary font-light leading-relaxed mt-3">
            The framework discussed in this book is educational and informational. It's not a clinical protocol, not therapy, not psychiatric treatment, not a diagnosis, and not individualized medical guidance. The book is general education for the public.
          </p>
          <ul className="text-sm text-secondary font-light leading-relaxed mt-3 list-disc pl-5 space-y-2">
            <li>The book does not promise specific outcomes, symptom reduction, or clinical results.</li>
            <li>The book is not a substitute for professional mental health care.</li>
            <li>Readers should consult their own licensed clinicians regarding diagnosis or treatment.</li>
            <li>The book is intended for general adult audiences and is not intended for use by minors without appropriate adult guidance.</li>
            <li>If you are in crisis, call or text 988 in the United States, call 911, or go to the nearest emergency room.</li>
          </ul>
        </div>
      </section>

      {/* Your next step in The Shrink Network */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-background border-t border-border/50">
        <div className="container mx-auto max-w-3xl">
          <NetworkNextStep destinations={["unstuck", "shrinkopedia", "shrinkmd"]} />
        </div>
      </section>
    </SiteLayout>
  );
}
